import { codeFromCodeableConcept } from '@seen/core/codeableConcept'
import {
  answersFitSideBySide,
  type CodingAnswerOption,
} from '@seen/core/codings'
import {
  BLOOD_PRESSURE_CODING,
  PAIN_SEVERITY_CODING,
} from '@seen/core/models/loinc'
import { ExtendedObservation } from '@seen/core/models/observation'
import { observationTemplate } from '@seen/core/terminology/observationTemplates'
import { cn } from '@seen/ui'
import { IconBan, IconChevronDown } from '@tabler/icons-react'
import type { JSX } from 'react'
import { hasDerivation } from '#src/components/rich-text-editor/ObservationNode/derived/derivedNode.ts'
import { answerTriggerClass } from '#src/components/rich-text-editor/ObservationNode/observationChip.ts'
import { SegmentedAnswers } from '#src/components/rich-text-editor/ObservationNode/SegmentedAnswers.tsx'
import type { ObservationNodeAttributes } from '#src/components/rich-text-editor/ObservationNode/types.ts'

const NO_OP = (): void => {}

// What a question's control looks like in the note, disabled: a template
// places questions and never answers them. Nothing in it fetches or records;
// its pointer events fall through to the row, which opens the question's
// configuration.
export function TemplateAnswerPreview({
  attributes,
}: {
  attributes: ObservationNodeAttributes
}): JSX.Element {
  return (
    <span className="group/preview inline-flex cursor-not-allowed items-center gap-1">
      <span className="inline-flex [&_*]:pointer-events-none">
        <PreviewControl attributes={attributes} />
      </span>
      <IconBan
        className="size-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover/preview:opacity-100 group-data-[engaged=true]/observation:opacity-100 group-[.ProseMirror-selectednode]:opacity-100"
        aria-hidden
      />
    </span>
  )
}

function PreviewControl({
  attributes,
}: {
  attributes: ObservationNodeAttributes
}): JSX.Element {
  const { scaleType } = attributes
  const answerOptions = attributes.answerOptions ?? []
  const choice = scaleType === 'Ord' || scaleType === 'Nom'

  if (attributes.repeats && answerOptions.length > 0 && !attributes.valueKind) {
    return (
      <AnswersPreview
        role="checkbox"
        options={answerOptions}
        placeholder="Please select any that apply..."
      />
    )
  }
  if (
    attributes.valueKind ||
    attributes.code === codeFromCodeableConcept(BLOOD_PRESSURE_CODING) ||
    attributes.code === codeFromCodeableConcept(PAIN_SEVERITY_CODING) ||
    observationTemplate(attributes) ||
    hasDerivation(attributes) ||
    !choice
  ) {
    return <PlaceholderBox placeholder={placeholderFor(attributes)} />
  }
  if (attributes.answerValueSet && answerOptions.length === 0) {
    return <SelectTrigger placeholder="Please select one..." />
  }
  return (
    <AnswersPreview
      role="radio"
      options={
        answerOptions.length > 0
          ? answerOptions
          : ExtendedObservation.getDefaultAnswerOptions(
              scaleType,
              attributes.codingDisplay
            )
      }
      placeholder="Please select one..."
    />
  )
}

function placeholderFor(attributes: ObservationNodeAttributes): string {
  if (attributes.scaleType === 'Nar') {
    return 'Add text...'
  }
  if (attributes.valueKind) {
    return 'Select a date...'
  }
  if (attributes.scaleType === 'Qn') {
    return attributes.unit ? `Add value (${attributes.unit})` : 'Add value...'
  }
  return 'Select...'
}

// The same decision the note makes (ObservationAnswerPicker): a few short
// answers side by side, otherwise a trigger.
function AnswersPreview({
  role,
  options,
  placeholder,
}: {
  role: 'radio' | 'checkbox'
  options: readonly CodingAnswerOption[]
  placeholder: string
}): JSX.Element {
  if (answersFitSideBySide(options.map((option) => option.display))) {
    return (
      <SegmentedAnswers
        role={role}
        options={options}
        isPicked={() => false}
        onClick={NO_OP}
        isDisabled={() => true}
      />
    )
  }
  return <SelectTrigger placeholder={placeholder} />
}

function SelectTrigger({ placeholder }: { placeholder: string }): JSX.Element {
  return (
    <button
      type="button"
      disabled
      tabIndex={-1}
      className={cn(answerTriggerClass(), 'disabled:opacity-100')}
    >
      {placeholder}
      <IconChevronDown className="size-4 shrink-0" />
    </button>
  )
}

function PlaceholderBox({ placeholder }: { placeholder: string }): JSX.Element {
  return (
    <span className={cn(answerTriggerClass(), 'w-[120px]')}>
      <span className="truncate">{placeholder}</span>
    </span>
  )
}
